/* eslint-disable max-classes-per-file --
   Two classes on purpose: `PreparationRejectedError` is a tiny custom
   Error type that only the bridge (below in this same file) throws and
   catches — splitting it into its own module would be more indirection
   than the two-line class body deserves. */
import type {
  ChatClient,
  Chip,
  Config,
  InboundEvent,
  OutboundEvent,
  WarmHandoffAdapter,
} from "@leagueplatform/chat-contract";
import { buildIntlProps, getCurrentLocale } from "@leagueplatform/locales";
import DEFAULT_AGENT_AVATAR from "assets/chat-agent-avatar.svg";
import {
  clearGenesysConversation,
  clearGenesysSession,
  clearSessionStartWatchdog,
  registerGenesysAuthProvider,
  resetGenesysConversation,
  sendGenesysMessage,
  sendGenesysQuickReply,
  setGenesysMessengerListener,
  setGenesysTyping,
  startGenesysConversation,
  terminateGenesysSession,
  waitForNextConversationCleared,
  wipeGenesysSessionState,
  type GenesysMessengerListener,
} from "./genesys-messenger";
import {
  clearRedirectHandoffStorage,
  redirectToGenesysAuth0,
  requestGenesysAuthCode,
} from "./genesys-oauth";
import {
  isOutsideHoursOfOperation,
  keepRoutingAttributesWithinBudget,
  stripInternalConfigKeys,
} from "./live-chat-config";
import { captureChatUnavailableMessages } from "./utils/capture-chat-unavailable-messages";
import { chatUnavailableStrings } from "./utils/chat-unavailable-strings";
import { CHAT_UNAVAILABLE_REASON } from "./utils/live-chat-constants";
import type { ChatUnavailableReason } from "./utils/live-chat-constants";

// V2 SYSTEM_MESSAGE ids the chat-contract adapter renders as system
// dividers ("<name> has joined/left the chat").
const SYSTEM_MSG_USER_JOINED = "USERNAME_HAS_JOINED_THE_CHAT";
const SYSTEM_MSG_USER_LEFT = "USERNAME_HAS_LEFT_THE_CHAT";

// Non-agent participant id for author-less system notices. Any non-user
// id on `INCOMING_MESSAGE.agent.id` renders as inbound.
const SYSTEM_AGENT_ID = "genesys-system";
const SYSTEM_AGENT_NAME = "";

const DEFAULT_AGENT_NAME = "EmblemHealth";

const SECONDS_PER_MINUTE = 60;

// Matches the V1→V2 adapter's per-message settlement window. If
// `sendMessage` hasn't settled by then, resolve the SDK promise so the
// composer bubble leaves the "sending" state.
const MESSAGE_SETTLE_TIMEOUT_MS = 3000;

// After preparation is accepted, wait this long for the SDK
// coordinator's activate() → post-handoff USER_START_CHAT chain to
// reach `handleUserStartChat`. If Aorta crashes mid-run, activate()
// never fires and the buffer would hold Genesys' bot messages
// invisibly — the fallback timer force-drains instead.
const HANDOFF_DRAIN_FALLBACK_MS = 10_000;

// Ceiling for the `conversationCleared` confirmation when clearing a
// conversation Genesys resumed during a warm handoff. Same reasoning as
// the adapter's `CONVERSATION_CLEARED_WAIT_MS`: a start dispatched before
// the confirmation lands is silently dropped.
const RESUMED_HANDOFF_CLEARED_WAIT_MS = 2_000;

// `MessagingService.clearSession` takes effect but never settles on UAT
// (observed 2026-10-19), so this wait always runs to the end. Kept short:
// it comes out of the adapter's 9.5s budget, which a slow Genesys boot
// (up to ~7s observed) already mostly spends.
const RESUMED_HANDOFF_CLEAR_SESSION_WAIT_MS = 300;

// Fallback when Genesys rejects `startConversation` without a message.
const CHAT_START_GENERIC_ERROR =
  "We couldn’t start the chat. Please try again.";

// iOS parity (PR 516, EMBLEM-8047) — tells the member how to get back
// to Eva from a live-agent chat. iOS sets this via
// `LeagueChatEngineConfig.disclaimerText` on the human-agent client
// so it's live-agent-only; on web the equivalent is this bridge
// emitting UPDATE_SESSION_CONFIG after CHAT_STARTED (Eva's client
// doesn't go through this file, so scoping is natural). Unconditionally
// replaces whatever `DISCLAIMER_TEXT` the BE meta carried — iOS does
// the same by setting the engine-level disclaimer regardless.
function liveAgentSwitchDisclaimerText(): string {
  const fallback =
    'You\'re currently in a chat with a live agent. To switch to Eva, our AI agent, end this chat and hit "Ask Eva" again.';
  try {
    const { messages } = buildIntlProps(getCurrentLocale());
    return messages.STR_LIVE_CHAT_SWITCH_TO_EVA_DISCLAIMER ?? fallback;
  } catch {
    return fallback;
  }
}

// Outcome of the warm-handoff preparation await.
//   ok:true             — fresh `.started` with attributes accepted
//   size_exceeded       — Genesys dropped the attribute map for size
//   session_unavailable — `.started(readOnly:true)` + reset refused, or
//                         a resumed old conversation we couldn't replace
type PreparationRejectionReason = "size_exceeded" | "session_unavailable";

type PreparationOutcome =
  | { ok: true }
  | { ok: false; reason: PreparationRejectionReason };

// Error thrown by `awaitPreparationDelivered`; mapped to `adapter_failed`
// by the coordinator. Not exported — internal bridge use only.
class PreparationRejectedError extends Error {
  readonly reason: PreparationRejectionReason;

  constructor(reason: PreparationRejectionReason) {
    super(`Genesys warm-handoff preparation rejected: ${reason}`);
    this.name = "PreparationRejectedError";
    this.reason = reason;
  }
}

const debug = (...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log("[bridge]", ...args);
  }
};

// Genesys' "message object" — narrowed defensively; the SDK docs
// defer to a linked page not mirrored in genesys-sdk.md.
type GenesysMessage = {
  id?: unknown;
  messageId?: unknown;
  text?: unknown;
  body?: unknown;
  direction?: unknown;
  from?: {
    id?: unknown;
    nickname?: unknown;
    image?: unknown;
    purpose?: unknown;
  };
  timestamp?: unknown;
  content?: unknown;
};

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

// Recognises the `{warmHandoffId, provider}` shape the SDK's session-
// machine passes to appClient's USER_START_CHAT AFTER
// `WarmHandoffCoordinator.activate` (rides on `event.meta` in V2).
function isPostHandoffInvocation(meta: unknown): boolean {
  if (!meta || typeof meta !== "object") return false;
  const record = meta as Record<string, unknown>;
  return (
    typeof record.warmHandoffId === "string" &&
    typeof record.provider === "string"
  );
}

function isAgentMessage(msg: GenesysMessage): boolean {
  const direction = readString(msg.direction);
  // Genesys uses server-side direction: "Inbound" = client→server (the
  // customer's own messages, which the SDK already renders from composer
  // state), "Outbound" = server→client (agent messages). Absent direction
  // is treated as "keep" — some system events (join banners) come through
  // without one.
  return direction?.toLowerCase() !== "inbound";
}

function extractRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

type QuickReplyMeta = {
  genesysQuickReply: { payload: string; action?: string };
};

type QuickReplyChip = Chip & { meta: QuickReplyMeta };

function readQuickReplyMeta(meta: unknown): QuickReplyMeta | undefined {
  const quickReply = extractRecord(extractRecord(meta)?.genesysQuickReply);
  if (!quickReply) return undefined;
  const payload = readString(quickReply.payload);
  if (payload === undefined) return undefined;
  return {
    genesysQuickReply: { payload, action: readString(quickReply.action) },
  };
}

function readQuickReplyChips(
  message: GenesysMessage
): QuickReplyChip[] | undefined {
  if (!Array.isArray(message.content)) return undefined;

  const chips = message.content.flatMap((entry): QuickReplyChip[] => {
    const record = extractRecord(entry);
    if (!record) return [];

    const contentType = readString(record.contentType);
    const option = extractRecord(
      contentType === "ButtonResponse"
        ? record.buttonResponse
        : record.quickReply
    );
    if (!option) return [];

    if (
      contentType === "ButtonResponse" &&
      readString(option.type)?.toLowerCase() !== "quickreply"
    ) {
      return [];
    }

    const text = readString(option.text);
    const payload = readString(option.payload);
    if (!text || payload === undefined) return [];

    return [
      {
        label: text,
        value: text,
        meta: {
          genesysQuickReply: { payload, action: readString(option.action) },
        },
      },
    ];
  });

  return chips.length > 0 ? chips : undefined;
}

// Suppresses the red divider these auth-failure strings would render
// in the transcript — the session-start watchdog surfaces
// CHAT_UNAVAILABLE right after, so the divider is redundant noise.
const AUTH_FAILURE_MESSAGE_PATTERNS: ReadonlyArray<string> = [
  "session authentication failed",
  "token has expired",
  "invalid token",
  "credentials are expired",
  "credentials are not authorized",
];

function isAuthFailureMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return AUTH_FAILURE_MESSAGE_PATTERNS.some((needle) => lower.includes(needle));
}

function readErrorMessage(payload: unknown): string | undefined {
  if (typeof payload === "string") return payload.trim() || undefined;
  if (payload instanceof Error) return payload.message || undefined;
  if (typeof payload === "object" && payload !== null) {
    const record = payload as Record<string, unknown>;
    const nested = extractRecord(record.data) ?? extractRecord(record.error);
    return (
      readString(record.body) ??
      readString(record.message) ??
      readString(record.errorDescription) ??
      readString(nested?.message) ??
      readString(nested?.body)
    );
  }
  return undefined;
}

// One-shot loop guard against server-side grant misconfig (fresh token
// still 403s). Set on redirect; cleared on `CHAT_STARTED`.
const AUTH_REDIRECT_ATTEMPTED_STORAGE_KEY =
  "emblem:live-chat:authRedirectAttempted";

function hasRecentlyRedirectedForAuth(): boolean {
  try {
    return (
      window.sessionStorage.getItem(AUTH_REDIRECT_ATTEMPTED_STORAGE_KEY) !==
      null
    );
  } catch {
    return false;
  }
}

function markAuthRedirectAttempted(): void {
  try {
    window.sessionStorage.setItem(
      AUTH_REDIRECT_ATTEMPTED_STORAGE_KEY,
      Date.now().toString()
    );
  } catch {
    // sessionStorage unavailable — loop guard just won't apply.
  }
}

function clearAuthRedirectAttempted(): void {
  try {
    window.sessionStorage.removeItem(AUTH_REDIRECT_ATTEMPTED_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — nothing to do.
  }
}

// Rounded up; floors at 1 minute so an already-elapsed notice doesn't
// count into the past.
function minutesUntilExpiry(expiresInSeconds: number): number {
  return Math.max(1, Math.ceil(expiresInSeconds / SECONDS_PER_MINUTE));
}

/**
 * V2-native `ChatClient` bridge over Genesys' headless SDK. Translates
 * Genesys' `MessagingService.*` events to V2 `InboundEvent`s and V2
 * `OutboundEvent`s to Genesys cxbus commands.
 */
export class EmblemLiveChatClient implements ChatClient {
  config: Config = {
    // Composer shows immediately (no "connecting" wait) so the
    // readOnly-restore recovery path can surface without the session-
    // machine wedging in `starting`.
    waitForAgent: false,
    incomingMessageStyle: "bubble",
  };

  // Set by chat-instance.ts; forwarded to the AI (Eva) chat client's
  // coordinator so Genesys handles the human side of AI→human transfer.
  warmHandoffAdapter?: WarmHandoffAdapter;

  private readonly subscribers = new Set<(event: InboundEvent) => void>();

  // Per-message outbound-settlement handles. Timeout fires as a fallback
  // so the composer bubble leaves "sending" if Genesys never settles.
  private readonly pendingSends = new Map<
    string,
    {
      resolve: () => void;
      reject: (err: Error) => void;
      timeout: ReturnType<typeof setTimeout>;
    }
  >();

  private currentAgentId: string | null = null;

  // Tracked alongside `currentAgentId` so the "user has left" divider
  // has a name to render on agent transfer.
  private currentAgentName: string | null = null;

  private endedByUser = false;

  // In-flight `clearGenesysConversation` from the last END_CHAT, awaited
  // by the next START_CHAT so Genesys doesn't reject the next start
  // with "There is already an active conversation."
  private pendingClear: Promise<void> | undefined;

  // Warm-handoff subscriber-less window. During
  // `warmHandoffAdapter.prepare`, emits from Genesys' bot land before
  // the session-machine has swapped activeClient to appClient — any
  // subscriber-emit is dropped. Buffer instead; flush on the post-
  // handoff USER_START_CHAT; discard on cancel.
  private handoffBuffering = false;

  private handoffBuffer: InboundEvent[] = [];

  // True between `.started(readOnly:false)` and any session-end signal.
  // Exposed via `hasActiveSession()` so the warm-handoff adapter can
  // refuse a preparation that would kill an active conversation.
  private sessionActive = false;

  // True from a direct-human `USER_START_CHAT` until Genesys answers
  // (`.started`, or any end / unavailable signal). Lets
  // `isConversationLive()` tell a chat that is still connecting from
  // one that is wedged with no conversation behind it.
  private startPending = false;

  // Set after we've deliberately surfaced `CHAT_UNAVAILABLE`. Gates
  // subsequent Genesys events so its own recovery attempts (`.restored`,
  // follow-up `.started`) don't override the "try later" surface.
  // Cleared on the next real user START_CHAT or on `onSessionCleared`.
  private bailedOut = false;

  // Warm-handoff proof-of-delivery await. Resolves on
  // `.started(newSession:true, readOnly:false)`; rejects on
  // `customAttributesSizeExceeded`. The latch remembers an outcome
  // that arrived before the adapter called `awaitPreparationDelivered`.
  private preparationLatch: PreparationOutcome | null = null;

  private preparationSettler: ((outcome: PreparationOutcome) => void) | null =
    null;

  // Cached custom attributes for readOnly-recovery restart. Also set
  // from `noteWarmHandoffAttributes` because the warm-handoff path
  // calls `startGenesysConversation` directly (bypassing
  // `handleUserStartChat`); without that, a recovery restart on the
  // handoff path would drop `warmTransferSummary` context.
  private lastStartCustomAttributes: Record<string, unknown> | undefined;

  private readOnlyRecoveryAttempted = false;

  // True from `beginPreparationTracking` until the preparation settles.
  // Scopes resumed-conversation recovery to the handoff's own start, so
  // a WS reconnect (`newSession:false`) after the handoff was accepted
  // isn't mistaken for a resumed old chat.
  private preparationPending = false;

  // Resumed-conversation recovery for the warm handoff (iOS parity,
  // emblem-health-ios#557). Genesys resumes the member's unfinished
  // conversation instead of opening a new one; that conversation never
  // sees the warm-transfer attributes. `clearing` while we clear it and
  // wait for the confirmation, `restarted` once the fresh start is out,
  // back to `idle` when the fresh conversation reports `newSession:true`.
  // One attempt per handoff; reset in `beginHandoffBuffering`.
  private resumedHandoffRecovery: "idle" | "clearing" | "restarted" = "idle";

  // Fallback drain timer for when the SDK coordinator's activate() →
  // post-handoff USER_START_CHAT chain never reaches us (Aorta agent-
  // run crash). Force-drains the buffer so QA at least sees what
  // Genesys delivered. See `HANDOFF_DRAIN_FALLBACK_MS`.
  private handoffDrainFallbackTimer: ReturnType<typeof setTimeout> | null =
    null;

  private readonly listener: GenesysMessengerListener;

  constructor() {
    // NOT booting the Genesys SDK here. With `auth.enabled: true`,
    // Genesys calls `getAuthCode` immediately after the AuthProvider
    // plugin registers — booting here would open the OAuth popup on
    // page load. Boot is lazy from the first START_CHAT.
    this.listener = this.buildListener();
    setGenesysMessengerListener(this.listener);

    // Plain variable assignment — does NOT trigger the OAuth popup.
    // Coupling to the bridge (not the CTA) means OAuth survives even
    // if the CTA never mounts (background resume of a session).
    registerGenesysAuthProvider(requestGenesysAuthCode);
  }

  handleOutboundEvent(event: OutboundEvent): Promise<void> | void {
    switch (event.type) {
      case "USER_START_CHAT":
        return this.handleUserStartChat(event.meta);
      case "USER_END_CHAT":
        return this.handleUserEndChat();
      case "OUTGOING_MESSAGE":
        return this.handleOutgoingMessage(event.id, event.body, event.meta);
      case "USER_TYPING_CHANGED":
        setGenesysTyping(event.isTyping);
        return undefined;
      case "USER_STOP_STREAM":
        // We don't stream — nothing to interrupt.
        return undefined;
      default:
        return undefined;
    }
  }

  subscribeToInboundEvents(
    callback: (event: InboundEvent) => void
  ): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private handleUserStartChat(meta: unknown): void {
    this.endedByUser = false;
    // The post-handoff USER_START_CHAT is the coordinator's automatic
    // dispatch, NOT a real user gesture — don't reset `bailedOut` on
    // it, or a post-handoff invocation could accidentally unblock
    // recovery events for a session already in CHAT_UNAVAILABLE.
    if (!isPostHandoffInvocation(meta)) {
      this.bailedOut = false;
    }

    // Post-handoff invocation: after `WarmHandoffCoordinator.activate`
    // succeeds, the session-machine re-enters `inSession` against
    // appClient and fires USER_START_CHAT with `{warmHandoffId,
    // provider}`. The adapter has already opened the Genesys
    // conversation with the full projection — running our normal
    // clear + startConversation would replace it with one carrying
    // only routing keys. Emit CHAT_STARTED synchronously.
    if (isPostHandoffInvocation(meta)) {
      // Order matters. Three rules together:
      //
      //   1. CHAT_STARTED must reach the SDK BEFORE any buffered
      //      inbound events, or the session-machine drops the
      //      INCOMING_MESSAGE / AGENT_TYPING_CHANGED events (they
      //      arrive before it transitions to active/ready) and the
      //      transition wedges.
      //   2. `this.emit()` still routes through the buffer while
      //      `handoffBuffering` is true — so `emit(CHAT_STARTED)` here
      //      would land *after* the buffered bot chatter and trigger
      //      rule-1's failure mode.
      //   3. The `onSessionStarted` handler that opened the Genesys
      //      conversation on the adapter's behalf ALSO called
      //      `this.emit(CHAT_STARTED)` AND
      //      `emitLiveAgentSwitchDisclaimer()` during the buffering
      //      window, so the buffer already contains a CHAT_STARTED and
      //      a UPDATE_SESSION_CONFIG. Replaying either after our direct
      //      emits would deliver a duplicate — the session-machine
      //      treats the second CHAT_STARTED as a state re-entry and
      //      re-runs its start-side effects, and a replayed disclaimer
      //      update is wasted work that re-merges the same string.
      //
      // Drain buffer (turns buffering off), deliver CHAT_STARTED
      // directly, re-emit the disclaimer directly, then replay the
      // rest in order (filtering both event types out of the replay).
      const buffered = this.drainHandoffBuffer();
      const replayable = buffered.filter(
        (buf) =>
          buf.type !== "CHAT_STARTED" && buf.type !== "UPDATE_SESSION_CONFIG"
      );
      this.deliverDirect({ id: crypto.randomUUID(), type: "CHAT_STARTED" });
      this.emitLiveAgentSwitchDisclaimer();
      replayable.forEach((buf) => this.deliverDirect(buf));
      return;
    }

    const configRecord = extractRecord(meta);
    // DEV-only escape hatch: skip the HOO gate when the response has
    // the messaging-BE extension's 4-key stripped-fallback shape
    // (AGENT_ORCHESTRATOR + ENTRY_POINT present, no welcome/member-id).
    // Lets QA exercise the Genesys transport past the extension's
    // fail-closed default. `import.meta.env.DEV` is dead-code-eliminated
    // by Vite, so this branch is physically absent from prod/staging.
    // See `project_emblem_7965_be_defensive_fallback.md`.
    const isBackendDefensiveFallback =
      import.meta.env.DEV &&
      configRecord !== undefined &&
      configRecord.AGENT_ORCHESTRATOR !== undefined &&
      configRecord.ENTRY_POINT !== undefined &&
      configRecord.CHAT_WELCOME_RICH_TEXT === undefined &&
      configRecord.DISCLAIMER_TEXT === undefined &&
      configRecord["Member ID"] === undefined;
    if (
      !isBackendDefensiveFallback &&
      isOutsideHoursOfOperation(configRecord)
    ) {
      this.emitChatUnavailable(CHAT_UNAVAILABLE_REASON.OUT_OF_HOURS);
      return;
    }

    // Over budget, Genesys would drop the whole map and the chat would
    // start with nothing to route the member on.
    const customAttributes = keepRoutingAttributesWithinBudget(
      stripInternalConfigKeys(configRecord)
    );
    this.lastStartCustomAttributes = customAttributes;
    this.readOnlyRecoveryAttempted = false;
    this.resumedHandoffRecovery = "idle";
    this.startPending = true;
    // Unconditional clear-before-start guarantees a clean server-side
    // state on every fresh START_CHAT. Genesys otherwise rejects
    // `startConversation` with "There is already an active conversation"
    // or resumes a disconnected one into read-only when a prior session
    // lingered server-side (page crash, missed close event, agent-side
    // disconnect, etc.). Safe to run unconditionally: the session-
    // priority guard in the bubble prevents START_CHAT from firing when
    // a live session already exists, so `clearConversation` never
    // destroys an active member conversation.
    const { pendingClear } = this;
    const doClear = () =>
      clearGenesysConversation()
        .catch((err) => {
          debug("USER_START_CHAT: pre-clear rejected (benign)", err);
        })
        .then(() => wipeGenesysSessionState());
    const doStart = () => startGenesysConversation(customAttributes);
    const startPromise = (pendingClear ?? Promise.resolve())
      .then(doClear)
      .then(doStart);
    startPromise.catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error("[bridge] startGenesysConversation rejected", err);
      this.startPending = false;
      // Terminal CHAT_ENDED (not SYSTEM_MESSAGE) so the session-machine
      // transitions out of `starting`. Chat-contract V1 adapter maps
      // `CHAT_ENDED { error }` to the same error screen as
      // CHAT_UNAVAILABLE.
      this.emit({
        id: crypto.randomUUID(),
        type: "CHAT_ENDED",
        error: readErrorMessage(err) ?? CHAT_START_GENERIC_ERROR,
      });
    });
  }

  private handleUserEndChat(): void {
    this.endedByUser = true;
    this.startPending = false;
    this.pendingClear = clearGenesysConversation()
      .catch((err) => {
        // Best-effort — SDK already transitioned to Ended locally.
        // eslint-disable-next-line no-console
        console.error(
          "[bridge] clearGenesysConversation rejected on END_CHAT",
          err
        );
      })
      .finally(() => {
        // Drop the conversation-active key so the Conversations plugin
        // can't restore this closed conversation on next start.
        wipeGenesysSessionState();
        this.pendingClear = undefined;
      });
  }

  private handleOutgoingMessage(
    messageId: string,
    body: string,
    meta?: unknown
  ): Promise<void> {
    const text = body.trim();
    if (!text) return Promise.resolve();
    const quickReply = readQuickReplyMeta(meta)?.genesysQuickReply;
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.settlePendingSend(messageId);
      }, MESSAGE_SETTLE_TIMEOUT_MS);
      this.pendingSends.set(messageId, { resolve, reject, timeout });
      const sent = quickReply
        ? sendGenesysQuickReply({ ...quickReply, text })
        : sendGenesysMessage(text);
      sent
        .then(() => {
          this.settlePendingSend(messageId);
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.error("[bridge] sendGenesysMessage rejected", err);
          this.settlePendingSend(
            messageId,
            new Error(readErrorMessage(err) ?? "Message failed to send")
          );
        });
    });
  }

  private settlePendingSend(messageId: string, error?: Error): void {
    const pending = this.pendingSends.get(messageId);
    if (!pending) return;
    clearTimeout(pending.timeout);
    this.pendingSends.delete(messageId);
    if (error) pending.reject(error);
    else pending.resolve();
  }

  private resolveAllPendingSends(): void {
    this.pendingSends.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.resolve();
    });
    this.pendingSends.clear();
  }

  private buildListener(): GenesysMessengerListener {
    return {
      onSessionStarted: (payload) => {
        if (this.bailedOut) return;
        this.startPending = false;
        // readOnly:true = Genesys resumed onto a disconnected
        // conversation. Escape via `resetConversation` (needs the
        // deployment's "Read-only conversation disconnect" flag). If
        // reset rejects, one-shot fallback: terminate + restart with
        // a fresh auth. Latched so a second readOnly can't loop.
        if (payload.readOnly) {
          this.sessionActive = true;
          resetGenesysConversation().catch((err) => {
            if (!this.readOnlyRecoveryAttempted) {
              this.readOnlyRecoveryAttempted = true;
              // Await termination so `clearConversation` settles
              // server-side before the retry runs — otherwise the
              // same active-conversation state can recreate the loop.
              terminateGenesysSession()
                .then(() =>
                  startGenesysConversation(this.lastStartCustomAttributes)
                )
                .catch((retryErr: unknown) => {
                  // eslint-disable-next-line no-console
                  console.error(
                    "[bridge] readOnly-recovery restart rejected",
                    retryErr
                  );
                  this.settlePreparation({
                    ok: false,
                    reason: "session_unavailable",
                  });
                  this.sessionActive = false;
                  this.emitChatUnavailable(
                    CHAT_UNAVAILABLE_REASON.SESSION_FAILURE
                  );
                });
              return;
            }
            // eslint-disable-next-line no-console
            console.error(
              "[bridge] readOnly recovery exhausted — emitting CHAT_UNAVAILABLE",
              err
            );
            // Settle FAILED (not ok) so the coordinator doesn't
            // activate and emit a post-handoff CHAT_STARTED that
            // would override CHAT_UNAVAILABLE.
            this.settlePreparation({
              ok: false,
              reason: "session_unavailable",
            });
            this.sessionActive = false;
            this.emitChatUnavailable(CHAT_UNAVAILABLE_REASON.SESSION_FAILURE);
          });
          return;
        }
        // Mid-preparation, `newSession:false` means Genesys resumed the
        // member's old conversation (`.started`, then `.restored`).
        // Clear it and restart with the handoff attributes instead of
        // surfacing it.
        if (
          !payload.newSession &&
          this.handoffBuffering &&
          this.preparationPending
        ) {
          this.recoverResumedHandoffConversation();
          return;
        }
        // Proof-of-delivery for the warm-handoff adapter — only settle
        // `ok:true` on a genuinely fresh session. `newSession:false`
        // means Genesys resumed a prior conversation (restored /
        // reconnected paths land here too), which does NOT carry the
        // freshly-staged warm-transfer attributes — accepting it would
        // mark the handoff successful while Genesys is still using the
        // old conversation cold. Leaving the latch unsettled lets the
        // adapter's timeout run its course and surface `adapter_timeout`
        // to the coordinator.
        if (payload.newSession) {
          if (this.resumedHandoffRecovery !== "idle") {
            // The restart landed after the adapter gave up (Eva already
            // told the member). Nobody is waiting for this conversation —
            // end it instead of surfacing it. Messages stay hidden until
            // the next start resets the recovery state.
            if (!this.handoffBuffering) {
              debug(
                "fresh conversation arrived after the handoff was abandoned — clearing it"
              );
              clearGenesysConversation().catch(() => undefined);
              return;
            }
            debug("fresh conversation started after clearing the resumed one");
            this.resumedHandoffRecovery = "idle";
          }
          this.settlePreparation({ ok: true });
        }
        this.sessionActive = true;
        clearAuthRedirectAttempted();
        this.emit({ id: crypto.randomUUID(), type: "CHAT_STARTED" });
        this.emitLiveAgentSwitchDisclaimer();
      },

      onMessagesReceived: (messages) => {
        if (this.bailedOut) return;
        // `.restored` transcript of the conversation being replaced.
        if (this.resumedHandoffRecovery !== "idle") return;
        messages
          .map((raw) => raw as GenesysMessage)
          .filter(isAgentMessage)
          .forEach((msg) => {
            const fromId = readString(msg.from?.id);
            const fromName = readString(msg.from?.nickname);
            const authorless = !fromId && !fromName;
            const agentName = authorless
              ? SYSTEM_AGENT_NAME
              : fromName ??
                (fromId === this.currentAgentId
                  ? this.currentAgentName
                  : null) ??
                DEFAULT_AGENT_NAME;
            const avatarUrl =
              readString(msg.from?.image) ?? DEFAULT_AGENT_AVATAR;

            // Agent-change detection — iOS parity. Fire on any
            // identity change (Genesys populates id or name, not
            // always both).
            const idChanged = !!fromId && fromId !== this.currentAgentId;
            const nameChanged =
              !authorless && agentName !== this.currentAgentName;
            if (idChanged || nameChanged) {
              if (this.currentAgentId || this.currentAgentName) {
                this.emit({
                  id: crypto.randomUUID(),
                  type: "SYSTEM_MESSAGE",
                  body: {
                    id: SYSTEM_MSG_USER_LEFT,
                    values: { username: this.currentAgentName ?? "" },
                  },
                });
              }
              this.emit({
                id: crypto.randomUUID(),
                type: "SYSTEM_MESSAGE",
                body: {
                  id: SYSTEM_MSG_USER_JOINED,
                  values: { username: agentName },
                },
              });
              this.currentAgentId = fromId ?? this.currentAgentId;
              this.currentAgentName = agentName;
            }

            const messageBody =
              readString(msg.text) ?? readString(msg.body) ?? "";
            const chips = readQuickReplyChips(msg);
            if (!messageBody && !chips) return;
            this.emit({
              id:
                readString(msg.id) ??
                readString(msg.messageId) ??
                crypto.randomUUID(),
              type: "INCOMING_MESSAGE",
              agent: {
                id: fromId ?? SYSTEM_AGENT_ID,
                name: agentName,
                avatarUrl,
              },
              body: { kind: "plain", content: messageBody },
              chips,
            });
          });
      },

      onAgentTyping: () => {
        if (!this.currentAgentId) return;
        this.emit({
          id: crypto.randomUUID(),
          type: "AGENT_TYPING_CHANGED",
          name: this.currentAgentName ?? "",
          isTyping: true,
        });
      },

      onAgentTypingTimeout: () => {
        if (!this.currentAgentId) return;
        this.emit({
          id: crypto.randomUUID(),
          type: "AGENT_TYPING_CHANGED",
          name: this.currentAgentName ?? "",
          isTyping: false,
        });
      },

      onSessionExpiring: (expiresInSeconds) => {
        // V2 has no dedicated expiry event; deliver as INCOMING_MESSAGE
        // with a system-agent id so it lands as an inbound bubble
        // (same channel V1 used).
        const minutes = minutesUntilExpiry(expiresInSeconds);
        this.emit({
          id: crypto.randomUUID(),
          type: "INCOMING_MESSAGE",
          agent: { id: SYSTEM_AGENT_ID, name: SYSTEM_AGENT_NAME },
          body: {
            kind: "plain",
            content: `Your chat session will end in about ${minutes} minute${
              minutes === 1 ? "" : "s"
            } of inactivity.`,
          },
        });
      },

      onSessionEnded: () => {
        this.emit({
          id: crypto.randomUUID(),
          type: "CHAT_ENDED",
          // V2: `error` is only meaningful on CHAT_UNAVAILABLE /
          // CONNECTION_LOST; ordinary disconnects leave it undefined.
        });
        // Only path that clears continuation state for agent-close /
        // Genesys-side disconnect (they don't trigger END_CHAT).
        wipeGenesysSessionState();
        this.sessionActive = false;
        this.startPending = false;
        this.currentAgentId = null;
        this.currentAgentName = null;
        this.endedByUser = false;
        this.resolveAllPendingSends();
      },

      onSessionCleared: () => {
        this.sessionActive = false;
        this.startPending = false;
        this.currentAgentId = null;
        this.currentAgentName = null;
        this.endedByUser = false;
        // Server confirmed clear — the readOnly state that triggered
        // bail-out is gone; let subsequent Genesys events flow.
        this.bailedOut = false;
      },

      onSessionFailure: (payload) => {
        // 401/403 from background token refresh or Auth.authError.
        // Loop guard: a recurrence after redirect points to a
        // server-side grant misconfig — no client redirect helps.
        if (hasRecentlyRedirectedForAuth()) {
          // eslint-disable-next-line no-console
          console.error(
            "[bridge] session-failure recurred after redirect — likely a Genesys grant misconfig, ending chat as unavailable",
            payload
          );
          this.emitChatUnavailable(CHAT_UNAVAILABLE_REASON.SESSION_FAILURE);
          return;
        }
        markAuthRedirectAttempted();
        redirectToGenesysAuth0().catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.error(
            "[bridge] redirectToGenesysAuth0 rejected — cannot recover auth automatically",
            err
          );
        });
      },

      onAuthLoggedOut: () => {
        // No credential path back without a fresh app login. Not a
        // session-failure recovery — silent iframe against a dead SSO
        // cookie would just loop.
        wipeGenesysSessionState();
        this.sessionActive = false;
        this.emitChatUnavailable(CHAT_UNAVAILABLE_REASON.AUTH_ENDED);
      },

      onCustomAttributesSizeExceeded: (payload) => {
        // Genesys drops the WHOLE attribute map — adapter must fail
        // rather than land the agent cold.
        // eslint-disable-next-line no-console
        console.error(
          "[bridge] onCustomAttributesSizeExceeded — settling preparation as rejected",
          payload
        );
        this.settlePreparation({ ok: false, reason: "size_exceeded" });
      },

      onError: (payload) => {
        const message = readErrorMessage(payload);
        if (!message) {
          // Suppress — the SDK UI has no fallback text; empty divider
          // is worse than nothing.
          // eslint-disable-next-line no-console
          console.error(
            "[bridge] onError (unparseable payload, suppressed)",
            payload
          );
          return;
        }
        // Suppress session-auth failures — the watchdog will surface
        // CHAT_UNAVAILABLE moments later; a red mid-transcript divider
        // is redundant noise.
        if (isAuthFailureMessage(message)) {
          // eslint-disable-next-line no-console
          console.error(
            "[bridge] onError session-auth failure — suppressed from transcript, watchdog will surface CHAT_UNAVAILABLE",
            payload
          );
          return;
        }
        this.emit({
          id: crypto.randomUUID(),
          type: "SYSTEM_MESSAGE",
          level: "error",
          body: message,
        });
      },
    };
  }

  /**
   * Called by the warm-handoff adapter when it starts `prepare`. Any
   * inbound events emitted between here and `drainHandoffBuffer` /
   * `discardHandoffBuffer` are queued rather than forwarded to
   * subscribers, which don't yet have a live subscription for the
   * appClient session.
   *
   * Also clears the `bailedOut` flag. The adapter's transport calls
   * (`startGenesysConversation`, etc.) bypass the bridge's
   * `handleOutboundEvent` path — so the "user's new gesture clears
   * bail-out" reset that lives in USER_START_CHAT doesn't fire on
   * adapter-driven starts. Without this, any handoff attempt that
   * follows a prior CHAT_UNAVAILABLE surface (bubble Auth.loggedOut,
   * readOnly-reset reject, session-start watchdog Auth.logout)
   * silently drops every Genesys event via the bailed-out gate in
   * `onSessionStarted` / `onMessagesReceived` — the transport works
   * but the SDK sees nothing.
   */
  beginHandoffBuffering(): void {
    debug("beginHandoffBuffering — subscriber-less window opened");
    this.handoffBuffering = true;
    this.handoffBuffer = [];
    this.bailedOut = false;
    // Fresh handoff — reset the once-per-attempt readOnly-recovery latch
    // so a repeat handoff after an earlier readOnly-recovery attempt can
    // try the reset+restart escape again. Warm handoff bypasses
    // `handleUserStartChat` (where this latch is otherwise cleared), so
    // without this every handoff after the first readOnly hit would skip
    // the recovery path and immediately surface CHAT_UNAVAILABLE.
    this.readOnlyRecoveryAttempted = false;
    this.resumedHandoffRecovery = "idle";
    // A fresh handoff supersedes any pending fallback drain from a prior
    // attempt.
    this.clearHandoffDrainFallback();
  }

  /**
   * Empties the handoff buffer and returns whatever was queued in
   * arrival order. Callers deliver `CHAT_STARTED` themselves before
   * replaying — see the post-handoff USER_START_CHAT branch for why the
   * ordering matters.
   */
  private drainHandoffBuffer(): InboundEvent[] {
    if (!this.handoffBuffering) return [];
    const queued = this.handoffBuffer;
    debug(`drainHandoffBuffer — draining ${queued.length} buffered event(s)`);
    this.handoffBuffering = false;
    this.handoffBuffer = [];
    // Real drain reached us — no need for the fallback to fire.
    this.clearHandoffDrainFallback();
    return queued;
  }

  /**
   * Discards the handoff buffer without forwarding. Called when the
   * adapter cancels / rejects the handoff — the buffered events belong
   * to a Genesys conversation the SDK isn't going to open.
   *
   * Also cancels any pending session-start watchdog. Rationale: the
   * adapter's own `withTimeout(PREPARE_TIMEOUT_MS = 9500)` fires 500 ms
   * before our 10 s watchdog. When adapter times out first, it reports
   * `adapter_timeout` to Eva's coordinator, which surfaces the "I
   * couldn't connect you with a rep" fallback message. If our watchdog
   * then fires 500 ms later it dispatches `Auth.logout` →
   * `Auth.loggedOut` → `emitChatUnavailable`, which closes the whole
   * chat and overrides Eva's fallback. Disarming here keeps the handoff-
   * failure UX in Eva's hands.
   */
  discardHandoffBuffer(): void {
    if (!this.handoffBuffering) return;
    debug(
      `discardHandoffBuffer — dropping ${this.handoffBuffer.length} buffered event(s)`
    );
    this.handoffBuffering = false;
    this.handoffBuffer = [];
    this.preparationPending = false;
    // Reset `sessionActive` — if `.started` fired inside the abandoned
    // buffering window, `onSessionStarted` will have flipped this true,
    // and the adapter's `hasActiveSession` preflight would then refuse
    // every subsequent handoff prep with `adapter_failed`.
    this.sessionActive = false;
    clearSessionStartWatchdog();
    this.clearHandoffDrainFallback();
  }

  /**
   * True while a Genesys conversation is live and writable (i.e.
   * between `.started(readOnly:false)` and `.conversationDisconnected`
   * / `.conversationCleared` / `Auth.loggedOut`). The warm-handoff
   * adapter calls this to refuse a preparation that would kill the
   * member's current live chat (iOS parity).
   */
  hasActiveSession(): boolean {
    return this.sessionActive;
  }

  /**
   * True when there is a Genesys conversation behind the chat panel, or
   * one on its way: live, still connecting after `USER_START_CHAT`
   * (bounded by the session-start watchdog), or mid warm-handoff.
   * False with the panel still `inSession` on this client means the
   * session is wedged — no conversation, and no signal coming that
   * would end it. `openChatSession` uses this to end only that case
   * and resume every other (EMBLEM-9452).
   */
  isConversationLive(): boolean {
    return this.sessionActive || this.startPending || this.handoffBuffering;
  }

  /**
   * Arms the preparation-outcome latch. Called by the warm-handoff
   * adapter BEFORE `startGenesysConversation` so a signal that fires
   * during the command (e.g. a very fast `.started`) doesn't slip past
   * the `awaitPreparationDelivered` window.
   */
  beginPreparationTracking(): void {
    debug("beginPreparationTracking — preparation-outcome latch armed");
    this.preparationLatch = null;
    this.preparationPending = true;
    // Drop any prior settler — a new preparation supersedes it. The old
    // settler's promise stays pending; the adapter's own timeout will
    // surface that as a preparation timeout on the caller side.
    this.preparationSettler = null;
  }

  /**
   * Records the custom attributes the warm-handoff adapter is about to
   * pass to `startGenesysConversation`. The adapter fires
   * `startGenesysConversation(attributes)` directly (bypassing
   * `handleUserStartChat`), so without this call the bridge's readOnly-
   * recovery path — which re-runs `startGenesysConversation` with
   * `this.lastStartCustomAttributes` after a terminate — would restart
   * with `undefined` and drop the `warmTransferSummary` /
   * `warmTransferCurrentNeed` context the agent needs to pick up the
   * chat with continuity. Call this immediately before the
   * adapter's own `startGenesysConversation`.
   *
   * The real fix is Genesys admin flipping the "Read-only conversation
   * disconnect" flag on the deployment so the readOnly-recovery path
   * doesn't need to fire in the first place — see
   * `project_emblem_7965_genesys_refresh_403.md` (RC-3).
   */
  noteWarmHandoffAttributes(
    attributes: Record<string, unknown> | undefined
  ): void {
    this.lastStartCustomAttributes = attributes;
  }

  /**
   * Resolves when Genesys confirms the conversation is up with the
   * staged attributes. Rejects with `PreparationRejectedError` when
   * Genesys drops the attribute map for size or reports the session as
   * `readOnly:true` and our reset escape is refused.
   *
   * Latched: if a signal fired before this was called (between
   * `beginPreparationTracking` and here), the latched outcome resolves
   * synchronously on the next tick instead of racing.
   */
  awaitPreparationDelivered(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.preparationLatch) {
        const latched = this.preparationLatch;
        this.preparationLatch = null;
        if (latched.ok) resolve();
        else reject(new PreparationRejectedError(latched.reason));
        return;
      }
      this.preparationSettler = (outcome) => {
        this.preparationSettler = null;
        if (outcome.ok) resolve();
        else reject(new PreparationRejectedError(outcome.reason));
      };
    });
  }

  private settlePreparation(outcome: PreparationOutcome): void {
    this.preparationPending = false;
    // Signal delivery: hand to an awaiting caller if there is one,
    // otherwise latch for the next `awaitPreparationDelivered` call.
    if (this.preparationSettler) {
      const settle = this.preparationSettler;
      this.preparationSettler = null;
      settle(outcome);
    } else {
      this.preparationLatch = outcome;
    }
    // On a successful settle, arm the fallback drain in case the SDK's
    // coordinator activate() → post-handoff USER_START_CHAT chain never
    // reaches us (Aorta agent-run crashes before emitting
    // WARM_HANDOFF_TERMINAL_RECEIVED). See `HANDOFF_DRAIN_FALLBACK_MS`.
    if (outcome.ok && this.handoffBuffering) {
      this.armHandoffDrainFallback();
    }
  }

  /**
   * Clears the conversation Genesys resumed during a warm handoff and
   * starts a fresh one with the handoff attributes. A clear Genesys
   * refuses fails the preparation so Eva can tell the member instead of
   * handing them to an agent without context.
   */
  private recoverResumedHandoffConversation(): void {
    // `.restored` follows `.started` for the same resumed conversation,
    // ~1.4s later on UAT — often after the clear has already finished
    // and the restart is out. Only one attempt per handoff: if the
    // restart itself resumes, the adapter's timeout reports it.
    if (this.resumedHandoffRecovery !== "idle") {
      debug(
        `ignoring resumed-session event while ${this.resumedHandoffRecovery}`
      );
      return;
    }
    debug("warm handoff resumed an old conversation — clearing + restarting");
    this.resumedHandoffRecovery = "clearing";
    // Anything buffered so far belongs to the old conversation.
    this.handoffBuffer = [];
    const cleared = waitForNextConversationCleared(
      RESUMED_HANDOFF_CLEARED_WAIT_MS
    );
    clearGenesysConversation()
      .then(() => cleared)
      .then((confirmed) => {
        debug(`resumed conversation cleared (confirmed=${confirmed})`);
        // Clearing the conversation leaves the session; a restart on it
        // is `.restored` with nothing to start the flow. Best-effort: on
        // reject the restart still runs and the timeout reports it.
        return new Promise<void>((resolve) => {
          const timer = setTimeout(() => {
            debug("clearSession did not settle — restarting anyway");
            resolve();
          }, RESUMED_HANDOFF_CLEAR_SESSION_WAIT_MS);
          clearGenesysSession()
            .then(
              () => debug("session cleared"),
              (err: unknown) => debug("clearSession rejected", err)
            )
            .finally(() => {
              clearTimeout(timer);
              resolve();
            });
        });
      })
      .then(() => {
        this.resumedHandoffRecovery = "restarted";
        // Handoff abandoned while we were clearing — don't open a
        // conversation nobody is waiting for.
        if (!this.handoffBuffering) return undefined;
        return startGenesysConversation(this.lastStartCustomAttributes).then(
          () => debug("restart accepted by Genesys")
        );
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error(
          "[bridge] clearing the resumed conversation for warm handoff failed",
          err
        );
        this.resumedHandoffRecovery = "restarted";
        this.settlePreparation({ ok: false, reason: "session_unavailable" });
      });
  }

  private armHandoffDrainFallback(): void {
    this.clearHandoffDrainFallback();
    this.handoffDrainFallbackTimer = setTimeout(() => {
      this.handoffDrainFallbackTimer = null;
      // Post-handoff USER_START_CHAT may have arrived between the timer
      // scheduling and its firing — drainHandoffBuffer would have cleared
      // handoffBuffering. Nothing to do if so.
      if (!this.handoffBuffering) return;
      debug(
        `handoff-drain fallback fired — post-handoff USER_START_CHAT never arrived (Aorta activate() likely wedged); force-draining ${this.handoffBuffer.length} buffered event(s)`
      );
      const buffered = this.drainHandoffBuffer();
      // Same ordering as the post-handoff branch of handleUserStartChat:
      // emit CHAT_STARTED first so the SDK's session-machine transitions
      // to active/ready, then replay every non-CHAT_STARTED buffered
      // event. Filter out any buffered CHAT_STARTED (F-da23) so the SDK
      // doesn't see the state-entry twice.
      const replayable = buffered.filter((buf) => buf.type !== "CHAT_STARTED");
      this.deliverDirect({ id: crypto.randomUUID(), type: "CHAT_STARTED" });
      replayable.forEach((buf) => this.deliverDirect(buf));
    }, HANDOFF_DRAIN_FALLBACK_MS);
  }

  private clearHandoffDrainFallback(): void {
    if (this.handoffDrainFallbackTimer !== null) {
      clearTimeout(this.handoffDrainFallbackTimer);
      this.handoffDrainFallbackTimer = null;
    }
  }

  // iOS PR 516 parity — fires UPDATE_SESSION_CONFIG right after each
  // CHAT_STARTED on the live-agent client so the SDK's session-machine
  // merges the switch-to-Eva disclaimer into `context.config`. Keeping
  // this on the client (not in meta / pre-chat) matches iOS, which sets
  // the engine disclaimer unconditionally — the BE's own DISCLAIMER_TEXT
  // (if any) gets overridden.
  private emitLiveAgentSwitchDisclaimer(): void {
    this.emit({
      id: crypto.randomUUID(),
      type: "UPDATE_SESSION_CONFIG",
      sessionConfig: { disclaimerText: liveAgentSwitchDisclaimerText() },
    });
  }

  // Anything but out_of_hours is an unexpected end of the chat, so report
  // it; the reason tag groups the events.
  private emitChatUnavailable(reason?: ChatUnavailableReason): void {
    // Arm bail-out so any subsequent Genesys events (SDK's own recovery
    // attempts — `.restored`, follow-up `.started`, late
    // `.messagesReceived`) don't override the "try later" surface we're
    // about to show.
    this.bailedOut = true;
    // Force sessionActive false. Any path that surfaces CHAT_UNAVAILABLE
    // is definitively ending the session — otherwise the warm-handoff
    // adapter's `hasActiveSession()` guard refuses subsequent handoff
    // attempts with "member already in a live Genesys conversation",
    // even though there IS no live conversation any more. Stale-true
    // typically comes from `.restored` emitting after we bailed
    // (Genesys auto-resume) or from a session that ended without firing
    // `.conversationDisconnected` / `.conversationCleared`.
    this.sessionActive = false;
    this.startPending = false;
    captureChatUnavailableMessages(reason);
    const { title, body } = chatUnavailableStrings(reason);
    this.emit({
      id: crypto.randomUUID(),
      type: "CHAT_ENDED",
      error: { title, message: body },
    });
  }

  private emit(event: InboundEvent): void {
    if (this.handoffBuffering) {
      this.handoffBuffer.push(event);
      return;
    }
    this.deliverDirect(event);
  }

  private deliverDirect(event: InboundEvent): void {
    this.subscribers.forEach((subscriber) => subscriber(event));
  }
}

// Singleton — the SDK subscribes once at config load. This client
// carries only in-process transient state (current agent id, ended-by-
// user flag) that resets on session-ended, so a singleton is safe.
export const emblemLiveChatClient = new EmblemLiveChatClient();

/**
 * Vendor-neutral chat teardown facade. Call from the app's logout flow
 * to end any live chat + wipe the provider's local session tokens
 * before the auth redirect fires. Consumers should import this instead
 * of reaching into the transport module directly — the underlying
 * provider is a private implementation detail.
 *
 * Also drops two client-side markers that would otherwise leak into the
 * next member on the same tab:
 *   - the bridge's `authRedirectAttempted` sessionStorage flag (would
 *     make the next member's first Genesys auth failure land in the
 *     "already retried" branch → immediate CHAT_UNAVAILABLE);
 *   - the OAuth redirect handoff keys (`pendingRedirectCallback`,
 *     `pendingRedirectState`, `pendingRedirectVerifier`) — a stale
 *     callback could otherwise be consumed by the next member's first
 *     `requestGenesysAuthCode`.
 *
 * Returns a bounded promise: `terminateGenesysSession()`'s
 * `clearConversation` command is awaited so the server-side kill
 * actually completes before navigation aborts it, but capped at
 * `LOGOUT_TEARDOWN_TIMEOUT_MS` so a stalled vendor iframe can't hang
 * the logout indefinitely. Rejects from the transport are swallowed —
 * the synchronous storage wipes above are the load-bearing part; the
 * server-side clear is belt-and-suspenders.
 */
const LOGOUT_TEARDOWN_TIMEOUT_MS = 2000;

export function terminateEmblemLiveChatSession(): Promise<void> {
  clearAuthRedirectAttempted();
  clearRedirectHandoffStorage();
  const teardown = terminateGenesysSession().catch(() => undefined);
  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, LOGOUT_TEARDOWN_TIMEOUT_MS);
  });
  return Promise.race([teardown, timeout]);
}
